import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef } from "react";

const STATS = [
  {
    value: 100,
    suffix: "+",
    label: "Strategic Engagements",
    note: "across ministries, states & enterprises",
  },
  {
    value: 25,
    suffix: "+",
    label: "Institutional Partnerships",
    note: "long-term advisory mandates",
  },
  {
    value: 18,
    suffix: "",
    label: "States Reached",
    note: "from Kashmir to Kanyakumari",
  },
  {
    value: 10,
    suffix: "M+",
    label: "Citizens Impacted",
    note: "through programs we've architected",
  },
  {
    value: 5000,
    suffix: "+",
    label: "Workforce Enabled",
    note: "officers, faculty & professionals",
  },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1800, bounce: 0 });
  const display = useTransform(spring, (v) => {
    if (value >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    return Math.round(v).toString();
  });

  useEffect(() => {
    if (inView) mv.set(value);
  }, [inView, value, mv]);

  // For value === 10 with M+ suffix we want '10' — so we just set the raw value.
  return (
    <span ref={ref} className="inline-flex items-baseline">
      <motion.span>{display}</motion.span>
      <span className="text-gold">{suffix}</span>
    </span>
  );
};

export const ImpactStats = () => {
  return (
    <section
      data-testid="impact-stats-section"
      className="relative bg-navy text-ivory py-24 md:py-32 overflow-hidden grain"
    >
      <div
        className="absolute inset-0 opacity-20 bg-grid-faint"
        style={{ backgroundSize: "100px 100px" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy to-navy-700/80" />

      <div className="relative container-bharat">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <p className="overline text-gold">Impact in Numbers</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 max-w-3xl leading-[1.05]">
              Outcomes measured in{" "}
              <span className="italic text-gold">institutions, citizens</span>{" "}
              and decades.
            </h2>
          </div>
          <p className="text-ivory/65 max-w-md text-base leading-relaxed">
            Every engagement is designed around measurable national outcomes —
            from health system resilience to workforce readiness.
          </p>
        </div>

        <div className="hairline mb-10" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ivory/10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="bg-navy p-8 lg:p-10 group hover:bg-navy-700 transition-colors duration-500"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-serif-display text-5xl md:text-6xl tracking-tight">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-4 text-ivory text-sm font-medium tracking-wide">
                {s.label}
              </p>
              <p className="mt-1 text-ivory/55 text-xs">{s.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactStats;
